import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { C, alpha } from '../lib/theme.js';
import { Button, ErrorLine, PageHeader } from '../ui/ui.js';
import { apiFetch } from '../lib/api.js';
import { supabase } from '../lib/supabase.js';
import { useOrg } from '../app/OrgContext.js';
import { driveConfigured, pickImageFromDrive } from '../lib/googleDrive.js';
import {
  ASPECTS,
  BRAND,
  BRAND_FONT_SPECS,
  IDENTITY_LAYOUT,
  TEMPLATES,
  panBounds,
  renderTemplate,
  type FieldBox,
  type FieldLayout,
  type ImageTransform,
  type Template,
  type TemplateLayout,
  type TemplateValues,
} from '../lib/brandKit.js';
import type { Creative, MediaItem, Connections } from '../lib/types.js';

type LoadedImg = { source: HTMLImageElement; w: number; h: number };

// ── Compose session persistence ──────────────────────────────────────────────
// Values + toggles are kept PER TEMPLATE (switching templates never loses edits)
// and the whole session is saved to storage so leaving Compose — or the site —
// and coming back restores exactly where you were.
const STORE_KEY = 'basil:compose:v1';
interface Persisted {
  templateId: string;
  aspectId: string;
  selectedId: string | null;
  valuesByTemplate: Record<string, TemplateValues>;
  enabledByTemplate: Record<string, Record<string, boolean>>;
  layoutByTemplate: Record<string, TemplateLayout>;
  transform: ImageTransform;
}
function loadCompose(): Partial<Persisted> {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || '{}') as Partial<Persisted>;
  } catch {
    return {};
  }
}
function saveCompose(p: Persisted) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(p));
  } catch {
    /* storage unavailable — ignore */
  }
}
const seedValues = (t: Template): TemplateValues =>
  Object.fromEntries(t.fields.map((f) => [f.key, f.default]));
const seedEnabled = (t: Template): Record<string, boolean> =>
  Object.fromEntries(t.fields.map((f) => [f.key, true]));

/**
 * Compose — take a base image, set the brand fonts/marks onto it with a locked
 * template, export a finished PNG. Compositing, not generation (CREATIVE_PARADIGM
 * §3). Self-sufficient: pull media in here (upload / Library / Drive). When opened
 * from a Post (?creative=), Save attaches the finished image back onto that Post.
 */
export function Editor() {
  const { orgId } = useOrg();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const initial = useMemo(() => loadCompose(), []);

  const [raws, setRaws] = useState<MediaItem[]>([]);
  const [driveFolderId, setDriveFolderId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(
    params.get('media') ?? initial.selectedId ?? null,
  );
  const [img, setImg] = useState<LoadedImg | null>(null);
  const [imgError, setImgError] = useState<string | null>(null);
  const [ingesting, setIngesting] = useState(false);

  const [templateId, setTemplateId] = useState(initial.templateId ?? TEMPLATES[0]!.id);
  const template = useMemo(
    () => TEMPLATES.find((t) => t.id === templateId) ?? TEMPLATES[0]!,
    [templateId],
  );
  const [aspectId, setAspectId] = useState(initial.aspectId ?? ASPECTS[0]!.id);
  const aspect = useMemo(() => ASPECTS.find((a) => a.id === aspectId) ?? ASPECTS[0]!, [aspectId]);

  // Values + toggles kept per template + persisted (see loadCompose/saveCompose).
  const [valuesByTemplate, setValuesByTemplate] = useState<Record<string, TemplateValues>>(
    initial.valuesByTemplate ?? {},
  );
  const [enabledByTemplate, setEnabledByTemplate] = useState<
    Record<string, Record<string, boolean>>
  >(initial.enabledByTemplate ?? {});
  // Per-template text move/resize overrides + which block is currently selected.
  const [layoutByTemplate, setLayoutByTemplate] = useState<Record<string, TemplateLayout>>(
    initial.layoutByTemplate ?? {},
  );
  const [selField, setSelField] = useState<string | null>(null);
  const [hoverField, setHoverField] = useState<string | null>(null);
  const [boxes, setBoxes] = useState<FieldBox[]>([]);
  const [prefill, setPrefill] = useState<TemplateValues>({});
  const [fontsReady, setFontsReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ readyId: string; attached: boolean } | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [confirmStartOver, setConfirmStartOver] = useState(false);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);
  const [transform, setTransform] = useState<ImageTransform>(
    initial.transform ?? { zoom: 1, panX: 0, panY: 0 },
  );
  const dragRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  // Active text move/resize gesture (null when idle).
  const textDragRef = useRef<{
    mode: 'move' | 'resize' | 'width';
    key: string;
    startX: number;
    startY: number;
    start: FieldLayout;
    startBox: FieldBox;
    boxDispH: number;
  } | null>(null);
  // Center-snap guides shown while dragging a block onto a midline.
  const [snap, setSnap] = useState<{ x: boolean; y: boolean }>({ x: false, y: false });

  // The current template's values + toggles (defaults until edited).
  const values = valuesByTemplate[templateId] ?? seedValues(template);
  const enabled = enabledByTemplate[templateId] ?? seedEnabled(template);
  const EMPTY_LAYOUT: TemplateLayout = useMemo(() => ({}), []);
  const layout = layoutByTemplate[templateId] ?? EMPTY_LAYOUT;

  // Give the current template an editable entry without overwriting existing edits.
  useEffect(() => {
    setValuesByTemplate((m) => (m[templateId] ? m : { ...m, [templateId]: seedValues(template) }));
    setEnabledByTemplate((m) =>
      m[templateId] ? m : { ...m, [templateId]: seedEnabled(template) },
    );
  }, [templateId, template]);

  // A selected text block belongs to one template — drop the selection on switch.
  useEffect(() => {
    setSelField(null);
  }, [templateId]);

  // Click anywhere off the canvas (page, controls, anywhere) → deselect all text
  // blocks so their frames disappear and only the artwork shows.
  useEffect(() => {
    const onDocDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setSelField(null);
        setHoverField(null);
      }
    };
    document.addEventListener('pointerdown', onDocDown);
    return () => document.removeEventListener('pointerdown', onDocDown);
  }, []);

  // Persist the whole session so navigating away — or reloading — keeps everything.
  useEffect(() => {
    saveCompose({
      templateId,
      aspectId,
      selectedId,
      valuesByTemplate,
      enabledByTemplate,
      layoutByTemplate,
      transform,
    });
  }, [
    templateId,
    aspectId,
    selectedId,
    valuesByTemplate,
    enabledByTemplate,
    layoutByTemplate,
    transform,
  ]);

  // Reset crop/zoom only when the user actually switches base image (not on mount).
  const firstSel = useRef(true);
  useEffect(() => {
    if (firstSel.current) {
      firstSel.current = false;
      return;
    }
    setTransform({ zoom: 1, panX: 0, panY: 0 });
  }, [selectedId]);

  // Copy→canvas bridge: opened from a saved Post → prefill its words + image, and
  // remember the Post so Save can attach the finished image back onto it.
  const creativeId = params.get('creative');
  const [linkedPost, setLinkedPost] = useState<Creative | null>(null);
  useEffect(() => {
    if (!orgId || !creativeId) return;
    void apiFetch<{ creatives: Creative[] }>(`/api/orgs/${orgId}/creatives`)
      .then((d) => {
        const c = d.creatives.find((x) => x.id === creativeId);
        if (!c) return;
        setLinkedPost(c);
        // Map the Post's headline onto any template field named 'headline'/'quote'.
        setPrefill({ headline: c.headline ?? '', quote: c.headline ?? '' });
        if (c.media_id && !params.get('media')) setSelectedId(c.media_id);
      })
      .catch(() => {});
  }, [orgId, creativeId, params]);

  // Merge a bridge prefill (the Post's headline) into the CURRENT template only.
  useEffect(() => {
    if (!Object.keys(prefill).length) return;
    setValuesByTemplate((m) => {
      const cur = m[templateId] ?? seedValues(template);
      const keys = new Set(template.fields.map((f) => f.key));
      const applied: TemplateValues = {};
      for (const [k, v] of Object.entries(prefill)) if (keys.has(k) && v) applied[k] = v;
      if (!Object.keys(applied).length) return m;
      return { ...m, [templateId]: { ...cur, ...applied } };
    });
  }, [prefill, templateId, template]);

  // Preload the exact brand font weights before any canvas draw.
  useEffect(() => {
    let alive = true;
    Promise.all(BRAND_FONT_SPECS.map((s) => document.fonts.load(s)))
      .then(() => alive && setFontsReady(true))
      .catch(() => alive && setFontsReady(true)); // draw with fallbacks rather than hang
    return () => {
      alive = false;
    };
  }, []);

  // Remember the linked Drive folder so the picker opens straight into it.
  useEffect(() => {
    if (!orgId) return;
    void apiFetch<Connections>(`/api/orgs/${orgId}/connections`)
      .then((d) => setDriveFolderId(d.drive?.folder_id ?? null))
      .catch(() => {});
  }, [orgId]);

  // Load the org's raw media (images to style + videos to use as-is).
  const loadRaws = useCallback(() => {
    if (!orgId) return;
    void apiFetch<{ media: MediaItem[] }>(`/api/orgs/${orgId}/media?stage=raw`)
      .then((d) => {
        setRaws(d.media);
        if (!selectedId && d.media[0]) setSelectedId(d.media[0].id);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load media'));
  }, [orgId, selectedId]);
  useEffect(() => {
    loadRaws();
  }, [loadRaws]);

  const selected = useMemo(() => raws.find((r) => r.id === selectedId) ?? null, [raws, selectedId]);
  const isVideo = selected?.kind === 'video';

  // Decode the selected image (crossOrigin so the canvas stays exportable).
  // Video is not decoded — it plays in a <video> element, no editing yet.
  useEffect(() => {
    setImgError(null);
    if (!selected?.url || selected.kind === 'video') {
      setImg(null);
      return;
    }
    const el = new Image();
    el.crossOrigin = 'anonymous';
    let alive = true;
    el.onload = () => alive && setImg({ source: el, w: el.naturalWidth, h: el.naturalHeight });
    el.onerror = () => alive && setImgError('Could not load that image.');
    el.src = selected.url;
    return () => {
      alive = false;
    };
  }, [selected]);

  // A disabled field contributes neither its value nor its placeholder guide, so
  // it's fully hidden from the composite.
  const displayValues = useMemo(() => {
    const out: TemplateValues = {};
    for (const f of template.fields)
      out[f.key] = enabled[f.key] === false ? '' : (values[f.key] ?? '');
    return out;
  }, [template, values, enabled]);
  const displayPlaceholders = useMemo(() => {
    const out: TemplateValues = {};
    for (const f of template.fields)
      out[f.key] = enabled[f.key] === false ? '' : (f.placeholder ?? '');
    return out;
  }, [template, enabled]);

  // Repaint on any change to the composition; capture each text block's hit box.
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;
    cv.width = aspect.w;
    cv.height = aspect.h;
    const out: FieldBox[] = [];
    renderTemplate(ctx, templateId, displayValues, img, aspect.w, aspect.h, transform, {
      placeholders: displayPlaceholders,
      preview: true,
      layout,
      boxes: out,
    });
    // Guard against a render loop: only push new boxes when they actually change.
    setBoxes((prev) => (JSON.stringify(prev) === JSON.stringify(out) ? prev : out));
  }, [img, displayValues, displayPlaceholders, templateId, aspect, fontsReady, transform, layout]);

  // Bring a new base image in without leaving Compose (upload → raw library).
  async function ingestFile(file: File) {
    if (!orgId) return;
    if (!file.type.startsWith('image/')) {
      setError('Style image works with images only. Use a reel in Compose.');
      return;
    }
    setIngesting(true);
    setError(null);
    try {
      const { path, token } = await apiFetch<{ path: string; token: string }>(
        `/api/orgs/${orgId}/media/upload-url`,
        { method: 'POST', body: JSON.stringify({ mime: file.type }) },
      );
      const up = await supabase.storage.from('media').uploadToSignedUrl(path, token, file);
      if (up.error) throw up.error;
      const rec = await apiFetch<MediaItem>(`/api/orgs/${orgId}/media`, {
        method: 'POST',
        body: JSON.stringify({ path, mime: file.type }),
      });
      setRaws((l) => [rec, ...l.filter((x) => x.id !== rec.id)]);
      setSelectedId(rec.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setIngesting(false);
    }
  }
  async function ingestFromDrive() {
    try {
      const pick = await pickImageFromDrive(driveFolderId, orgId);
      if (pick) await ingestFile(pick.file);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Google Drive import failed');
    }
  }

  async function save() {
    const cv = canvasRef.current;
    if (!cv || !orgId || saving) return;
    setSaving(true);
    setError(null);
    setSaved(null);
    try {
      // Export a CLEAN frame — no placeholder guides, no disabled fields.
      const ctx = cv.getContext('2d');
      if (ctx)
        renderTemplate(ctx, templateId, displayValues, img, aspect.w, aspect.h, transform, {
          layout,
        });
      const blob = await new Promise<Blob | null>((res) => cv.toBlob(res, 'image/png'));
      // Restore the on-screen preview (with placeholder guides).
      if (ctx)
        renderTemplate(ctx, templateId, displayValues, img, aspect.w, aspect.h, transform, {
          placeholders: displayPlaceholders,
          preview: true,
          layout,
        });
      if (!blob) throw new Error('Could not render the image.');
      const mime = 'image/png';
      const { path, token } = await apiFetch<{ path: string; token: string }>(
        `/api/orgs/${orgId}/media/upload-url`,
        { method: 'POST', body: JSON.stringify({ mime }) },
      );
      const up = await supabase.storage.from('media').uploadToSignedUrl(path, token, blob);
      if (up.error) throw up.error;
      const title =
        (values.headline ?? values.wordmark ?? values.quote ?? '').split('\n')[0]?.slice(0, 80) ||
        'Untitled';
      const ready = await apiFetch<{ id: string }>(`/api/orgs/${orgId}/media`, {
        method: 'POST',
        body: JSON.stringify({ path, mime, stage: 'ready', source_media_id: selectedId, title }),
      });
      // Loop-close: if we came from a Post, attach the finished image onto it.
      if (linkedPost) {
        await apiFetch(`/api/orgs/${orgId}/creatives/${linkedPost.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ media_id: ready.id }),
        });
      }
      setSaved({ readyId: ready.id, attached: !!linkedPost });
      setToast(linkedPost ? '✓ Attached to your post' : '✓ Saved to your Library');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  // Video has no editing yet: from a Post, attach it; standalone, go write copy.
  async function useVideo() {
    if (!orgId || !selected || saving) return;
    if (!linkedPost) {
      navigate('/studio/copy');
      return;
    }
    setSaving(true);
    setError(null);
    setSaved(null);
    try {
      await apiFetch(`/api/orgs/${orgId}/creatives/${linkedPost.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ media_id: selected.id }),
      });
      setSaved({ readyId: selected.id, attached: true });
      setToast('✓ Attached to your post');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not attach video');
    } finally {
      setSaving(false);
    }
  }

  const setField = (k: string, val: string) => {
    setSaved(null);
    setValuesByTemplate((m) => ({
      ...m,
      [templateId]: { ...(m[templateId] ?? seedValues(template)), [k]: val },
    }));
  };

  // Keep pan within bounds so the frame never shows a gap.
  const clampPan = (px: number, py: number, zoom: number) => {
    if (!img) return { panX: px, panY: py };
    const { maxX, maxY } = panBounds(img.w, img.h, aspect.w, aspect.h, zoom);
    return { panX: Math.max(-maxX, Math.min(maxX, px)), panY: Math.max(-maxY, Math.min(maxY, py)) };
  };
  const setZoom = (z: number) => setTransform((t) => ({ zoom: z, ...clampPan(t.panX, t.panY, z) }));

  function onDragStart(e: React.PointerEvent<HTMLCanvasElement>) {
    setSelField(null); // clicking the photo (not a text block) deselects text
    if (!img) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, panX: transform.panX, panY: transform.panY };
  }
  function onDragMove(e: React.PointerEvent<HTMLCanvasElement>) {
    const d = dragRef.current;
    if (!d) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const scale = e.currentTarget.width / rect.width; // canvas px per display px
    setTransform((t) => ({
      ...t,
      ...clampPan(d.panX + (e.clientX - d.x) * scale, d.panY + (e.clientY - d.y) * scale, t.zoom),
    }));
  }
  const onDragEnd = () => {
    dragRef.current = null;
  };

  // ── Text block move + resize (overlay on the canvas) ──
  const patchLayout = (key: string, patch: Partial<FieldLayout>) =>
    setLayoutByTemplate((m) => {
      const cur = m[templateId] ?? {};
      const curF = cur[key] ?? IDENTITY_LAYOUT;
      return { ...m, [templateId]: { ...cur, [key]: { ...curF, ...patch } } };
    });
  const resetLayout = (key: string) =>
    setLayoutByTemplate((m) => {
      const cur = m[templateId] ?? {};
      return { ...m, [templateId]: { ...cur, [key]: { ...IDENTITY_LAYOUT } } };
    });

  // Wipe the whole composition back to a blank slate — image, text, toggles,
  // layout, crop, template and aspect. (Two-step confirmed; persistence follows.)
  function startOver() {
    setConfirmStartOver(false);
    setSelectedId(null);
    setImg(null);
    setImgError(null);
    setValuesByTemplate({});
    setEnabledByTemplate({});
    setLayoutByTemplate({});
    setTransform({ zoom: 1, panX: 0, panY: 0 });
    setTemplateId(TEMPLATES[0]!.id);
    setAspectId(ASPECTS[0]!.id);
    setSelField(null);
    setSaved(null);
    setError(null);
    setToast('Cleared — starting fresh.');
  }

  function onTextDown(
    e: React.PointerEvent,
    key: string,
    mode: 'move' | 'resize' | 'width',
    box: FieldBox,
  ) {
    e.preventDefault();
    e.stopPropagation();
    setSelField(key);
    setSaved(null);
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    const rect = wrapRef.current?.getBoundingClientRect();
    const boxDispH = rect ? (box.h / aspect.h) * rect.height : box.h;
    textDragRef.current = {
      mode,
      key,
      startX: e.clientX,
      startY: e.clientY,
      start: layout[key] ?? IDENTITY_LAYOUT,
      startBox: box,
      boxDispH: Math.max(1, boxDispH),
    };
  }
  function onTextMove(e: React.PointerEvent) {
    const d = textDragRef.current;
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!d || !rect) return;
    if (d.mode === 'move') {
      let dx = d.start.dx + (e.clientX - d.startX) / rect.width;
      let dy = d.start.dy + (e.clientY - d.startY) / rect.height;
      // Center-snap: if the block's center lands near a midline, snap onto it.
      const b = d.startBox;
      const leftC = b.x + (dx - d.start.dx) * aspect.w;
      const topC = b.y + (dy - d.start.dy) * aspect.h;
      const thX = aspect.w * 0.02;
      const thY = aspect.h * 0.02;
      const sx = Math.abs(leftC + b.w / 2 - aspect.w / 2) < thX;
      const sy = Math.abs(topC + b.h / 2 - aspect.h / 2) < thY;
      if (sx) dx = d.start.dx + (aspect.w / 2 - b.w / 2 - b.x) / aspect.w;
      if (sy) dy = d.start.dy + (aspect.h / 2 - b.h / 2 - b.y) / aspect.h;
      setSnap({ x: sx, y: sy });
      patchLayout(d.key, { dx, dy });
    } else if (d.mode === 'width') {
      // Drag the side handle to widen/narrow the wrap frame (changes line breaks).
      const start = d.start.widthMul ?? 1;
      const next = start + ((e.clientX - d.startX) / rect.width) * 2.5;
      patchLayout(d.key, { widthMul: Math.max(0.3, Math.min(4, next)) });
    } else {
      // Drag the corner down by one block-height ≈ double the size. Clamped.
      const growth = 1 + (e.clientY - d.startY) / d.boxDispH;
      patchLayout(d.key, { scale: Math.max(0.4, Math.min(4, d.start.scale * growth)) });
    }
  }
  const onTextUp = () => {
    textDragRef.current = null;
    setSnap({ x: false, y: false });
  };

  // The canvas display scale (canvas px → %) drives the overlay box positions.
  const pct = (n: number, total: number) => `${(n / total) * 100}%`;

  return (
    <div style={{ padding: '26px 30px 40px', maxWidth: 1120, animation: 'basilfade .3s ease' }}>
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 400,
            padding: '10px 18px',
            borderRadius: 10,
            background: C.surface,
            border: `1px solid ${alpha(C.good, 45)}`,
            color: C.good,
            fontSize: 13,
            fontWeight: 600,
            boxShadow: '0 12px 34px rgba(0,0,0,.45)',
            animation: 'basilfade .2s ease',
          }}
        >
          {toast}
        </div>
      )}
      <PageHeader
        eyebrow="Set the brand type onto the image"
        title="Style image"
        actions={
          confirmStartOver ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12.5 }}>
              <span style={{ color: C.muted }}>
                Clear the image, text and layout — are you sure?
              </span>
              <span
                onClick={() => startOver()}
                style={{ color: C.act, fontWeight: 600, cursor: 'pointer' }}
              >
                Yes, start over
              </span>
              <span
                onClick={() => setConfirmStartOver(false)}
                style={{ color: C.muted, cursor: 'pointer' }}
              >
                Cancel
              </span>
            </span>
          ) : (
            <span
              onClick={() => setConfirmStartOver(true)}
              title="Wipe everything and begin a fresh composition"
              style={{ fontSize: 12.5, color: C.secondary, cursor: 'pointer', fontWeight: 600 }}
            >
              ⟲ Start over
            </span>
          )
        }
      />
      {error && <ErrorLine>{error}</ErrorLine>}
      {imgError && <ErrorLine>{imgError}</ErrorLine>}

      {linkedPost && (
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            padding: '10px 14px',
            marginBottom: 16,
            borderRadius: 10,
            background: alpha(C.basil, 10),
            border: `1px solid ${alpha(C.basil, 30)}`,
            fontSize: 12.5,
          }}
        >
          <span style={{ color: C.basil, fontWeight: 600 }}>Styling a post</span>
          <span
            style={{
              color: C.muted,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            “{linkedPost.primary_text.slice(0, 90)}”
          </span>
          <span style={{ marginLeft: 'auto', color: C.faint, whiteSpace: 'nowrap' }}>
            Save attaches the {isVideo ? 'video' : 'image'} to this post
          </span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 320px', gap: 22 }}>
        {/* ── Canvas preview ── */}
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: 18,
              background: C.surface2,
              border: `1px solid ${C.line}`,
              borderRadius: 12,
            }}
          >
            {isVideo ? (
              <video
                src={selected?.url ?? ''}
                controls
                style={{
                  width: '100%',
                  maxWidth: 380,
                  borderRadius: 6,
                  boxShadow: '0 10px 30px rgba(0,0,0,.35)',
                  background: '#000',
                }}
              />
            ) : (
              <div
                ref={wrapRef}
                style={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: aspect.w / aspect.h < 1 ? 360 : 460,
                  lineHeight: 0,
                }}
              >
                <canvas
                  ref={canvasRef}
                  onPointerDown={onDragStart}
                  onPointerMove={onDragMove}
                  onPointerUp={onDragEnd}
                  onPointerLeave={onDragEnd}
                  style={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    borderRadius: 6,
                    boxShadow: '0 10px 30px rgba(0,0,0,.35)',
                    background: BRAND.palette.forest,
                    cursor: img ? (dragRef.current ? 'grabbing' : 'grab') : 'default',
                    touchAction: 'none',
                  }}
                />
                {/* Draggable / resizable text blocks (RECODED corner mark is fixed) */}
                {boxes.map((b) => {
                  const isSel = b.key === selField;
                  const isHover = b.key === hoverField;
                  return (
                    <div
                      key={b.key}
                      onPointerDown={(e) => onTextDown(e, b.key, 'move', b)}
                      onPointerMove={onTextMove}
                      onPointerUp={onTextUp}
                      onPointerEnter={() => setHoverField(b.key)}
                      onPointerLeave={() => setHoverField((k) => (k === b.key ? null : k))}
                      title="Drag to move"
                      style={{
                        position: 'absolute',
                        left: pct(b.x, aspect.w),
                        top: pct(b.y, aspect.h),
                        width: pct(b.w, aspect.w),
                        height: pct(b.h, aspect.h),
                        minWidth: 10,
                        minHeight: 10,
                        boxSizing: 'border-box',
                        // Only show the frame when this block is selected (or hovered);
                        // otherwise it stays invisible so the artwork reads clean.
                        border: isSel
                          ? `1px solid ${C.basil}`
                          : isHover
                            ? `1px dashed ${alpha(C.basil, 45)}`
                            : '1px solid transparent',
                        borderRadius: 3,
                        background: isSel ? alpha(C.basil, 12) : 'transparent',
                        cursor: 'move',
                        touchAction: 'none',
                      }}
                    >
                      {isSel && (
                        <>
                          {/* corner: overall size */}
                          <span
                            onPointerDown={(e) => onTextDown(e, b.key, 'resize', b)}
                            onPointerMove={onTextMove}
                            onPointerUp={onTextUp}
                            title="Drag to resize the text"
                            style={{
                              position: 'absolute',
                              right: -7,
                              bottom: -7,
                              width: 13,
                              height: 13,
                              borderRadius: '50%',
                              background: C.basil,
                              border: '2px solid #fff',
                              boxShadow: '0 1px 4px rgba(0,0,0,.4)',
                              cursor: 'nwse-resize',
                              touchAction: 'none',
                            }}
                          />
                          {/* right edge: frame width (line breaks) */}
                          <span
                            onPointerDown={(e) => onTextDown(e, b.key, 'width', b)}
                            onPointerMove={onTextMove}
                            onPointerUp={onTextUp}
                            title="Drag to widen or narrow the text box"
                            style={{
                              position: 'absolute',
                              right: -6,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              width: 8,
                              height: 22,
                              borderRadius: 4,
                              background: C.basil,
                              border: '2px solid #fff',
                              boxShadow: '0 1px 4px rgba(0,0,0,.4)',
                              cursor: 'ew-resize',
                              touchAction: 'none',
                            }}
                          />
                        </>
                      )}
                    </div>
                  );
                })}
                {/* Center-snap guide lines (only while snapped mid-drag) */}
                {snap.x && (
                  <div
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: 0,
                      bottom: 0,
                      width: 1,
                      transform: 'translateX(-0.5px)',
                      background: alpha(C.basil, 70),
                      pointerEvents: 'none',
                    }}
                  />
                )}
                {snap.y && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: 0,
                      right: 0,
                      height: 1,
                      transform: 'translateY(-0.5px)',
                      background: alpha(C.basil, 70),
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </div>
            )}
          </div>

          {/* Position + zoom the base image inside the frame */}
          {img && (
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ font: `500 10px 'IBM Plex Mono',monospace`, color: C.faint }}>
                ZOOM
              </span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={transform.zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                style={{ flex: 1, accentColor: C.basil, cursor: 'pointer' }}
              />
              <span
                style={{ font: `500 10px 'IBM Plex Mono',monospace`, color: C.muted, width: 34 }}
              >
                {transform.zoom.toFixed(1)}×
              </span>
              <button
                type="button"
                onClick={() => setTransform({ zoom: 1, panX: 0, panY: 0 })}
                style={{
                  padding: '5px 10px',
                  borderRadius: 7,
                  border: `1px solid ${C.line}`,
                  background: C.surface,
                  color: C.secondary,
                  fontFamily: 'inherit',
                  fontSize: 11.5,
                  cursor: 'pointer',
                }}
              >
                Reset
              </button>
            </div>
          )}
          {!isVideo && (
            <div
              style={{
                marginTop: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap',
                minHeight: 24,
              }}
            >
              {selField ? (
                <>
                  <span style={{ fontSize: 11.5, color: C.basil, fontWeight: 600 }}>
                    Editing “{template.fields.find((f) => f.key === selField)?.label ?? selField}” —
                    drag to move · corner = size · side = width
                  </span>
                  <span
                    style={{
                      font: `500 10px 'IBM Plex Mono',monospace`,
                      color: C.muted,
                      width: 40,
                    }}
                  >
                    {Math.round((layout[selField]?.scale ?? 1) * 100)}%
                  </span>
                  <button type="button" onClick={() => resetLayout(selField)} style={miniBtnStyle}>
                    Reset
                  </button>
                  <button type="button" onClick={() => setSelField(null)} style={miniBtnStyle}>
                    Done
                  </button>
                </>
              ) : (
                <span style={{ fontSize: 11, color: C.faint }}>
                  Tap any text to move or resize it
                  {img ? ' · drag the photo to reposition the crop' : ''}.
                </span>
              )}
            </div>
          )}
          {!isVideo && (
            <div style={{ marginTop: 4, fontSize: 10.5, color: C.faint, fontStyle: 'italic' }}>
              Prompt text like “Write text here” is a guide — it’s never saved into the exported
              image. Turn a field off (toggle) to leave it out.
            </div>
          )}

          {/* Base image — pull one in (upload / Drive) or pick from raw library */}
          <div style={{ marginTop: 14 }}>
            <div
              style={{
                font: `500 10px 'IBM Plex Mono',monospace`,
                color: C.faint,
                marginBottom: 8,
              }}
            >
              BASE IMAGE
            </div>
            <div style={{ fontSize: 11, color: C.faint, marginBottom: 8 }}>
              Images only here — Style image sets type onto a still. Got a reel?{' '}
              <span
                onClick={() => navigate('/studio/copy')}
                style={{ color: C.basil, fontWeight: 600, cursor: 'pointer' }}
              >
                Use it in Compose →
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                gap: 8,
                overflowX: 'auto',
                paddingBottom: 4,
                alignItems: 'center',
              }}
            >
              <label
                title="Upload an image"
                style={{
                  ...tileStyle,
                  borderStyle: 'dashed',
                  color: C.basil,
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <span style={{ fontSize: 18 }}>{ingesting ? '…' : '↑'}</span>
                <span style={{ fontSize: 8.5 }}>UPLOAD</span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  style={{ display: 'none' }}
                  disabled={ingesting}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void ingestFile(f);
                  }}
                />
              </label>
              {driveConfigured && (
                <button
                  type="button"
                  onClick={() => void ingestFromDrive()}
                  title="Import from Google Drive"
                  style={{ ...tileStyle, color: C.muted, flexDirection: 'column', gap: 2 }}
                >
                  <span style={{ fontSize: 15 }}>▲</span>
                  <span style={{ fontSize: 8.5 }}>DRIVE</span>
                </button>
              )}
              {raws.map((r) => {
                const thumbStyle: React.CSSProperties = {
                  width: 60,
                  height: 60,
                  objectFit: 'cover',
                  borderRadius: 8,
                  cursor: 'pointer',
                  flexShrink: 0,
                  display: 'block',
                  border: `2px solid ${r.id === selectedId ? C.basil : 'transparent'}`,
                  opacity: r.id === selectedId ? 1 : 0.7,
                };
                return r.kind === 'video' ? (
                  <div
                    key={r.id}
                    onClick={() => setSelectedId(r.id)}
                    style={{ position: 'relative', width: 60, height: 60, flexShrink: 0 }}
                  >
                    <video src={r.url ?? ''} muted preload="metadata" style={thumbStyle} />
                    <span
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'rgba(255,255,255,.9)',
                        fontSize: 16,
                        pointerEvents: 'none',
                      }}
                    >
                      ▶
                    </span>
                  </div>
                ) : (
                  <img
                    key={r.id}
                    src={r.url ?? ''}
                    alt={r.alt ?? ''}
                    onClick={() => setSelectedId(r.id)}
                    style={thumbStyle}
                  />
                );
              })}
              {raws.length === 0 && (
                <span style={{ fontSize: 12, color: C.muted, whiteSpace: 'nowrap' }}>
                  Upload an image or pick a video to start.
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ── Controls ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {isVideo ? (
            <>
              <div
                style={{
                  padding: '14px 16px',
                  borderRadius: 10,
                  border: `1px solid ${C.line}`,
                  background: C.surface2,
                }}
              >
                <div
                  style={{
                    font: `600 11px 'IBM Plex Mono',monospace`,
                    color: C.basil,
                    marginBottom: 6,
                  }}
                >
                  VIDEO
                </div>
                <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.6 }}>
                  This clip posts as-is. Text &amp; motion editing for video is coming soon.
                </div>
              </div>
              <Button
                onClick={() => void useVideo()}
                style={{
                  background: C.basil,
                  color: C.basilInk,
                  borderColor: C.basil,
                  opacity: saving ? 0.6 : 1,
                }}
              >
                {linkedPost
                  ? saving
                    ? 'Attaching…'
                    : 'Use this video for the post'
                  : 'Write a caption →'}
              </Button>
              {saved?.attached && (
                <div style={{ fontSize: 12.5, color: C.good }}>
                  ✓ Attached to your post.{' '}
                  <span
                    onClick={() => navigate('/studio/library')}
                    style={{ color: C.basil, cursor: 'pointer' }}
                  >
                    View post →
                  </span>
                </div>
              )}
            </>
          ) : (
            <>
              <Field label="TEMPLATE">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {TEMPLATES.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTemplateId(t.id)}
                      style={{
                        textAlign: 'left',
                        padding: '9px 11px',
                        borderRadius: 9,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        border: `1px solid ${t.id === templateId ? C.basil : C.line}`,
                        background: t.id === templateId ? C.surface2 : C.surface,
                        color: C.primary,
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{t.name}</div>
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{t.desc}</div>
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="ASPECT">
                <div style={{ display: 'flex', gap: 6 }}>
                  {ASPECTS.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => setAspectId(a.id)}
                      style={{
                        flex: 1,
                        padding: '8px 0',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: 12,
                        border: `1px solid ${a.id === aspectId ? C.basil : C.line}`,
                        background: a.id === aspectId ? C.surface2 : C.surface,
                        color: C.primary,
                      }}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              </Field>

              {template.fields.map((f) => {
                const on = enabled[f.key] !== false;
                const toggle = () =>
                  setEnabledByTemplate((m) => {
                    const cur = m[templateId] ?? seedEnabled(template);
                    return { ...m, [templateId]: { ...cur, [f.key]: cur[f.key] === false } };
                  });
                return (
                  <Field key={f.key} label={f.label.toUpperCase()} on={on} onToggle={toggle}>
                    {f.multiline ? (
                      <textarea
                        value={values[f.key] ?? ''}
                        maxLength={f.maxLen}
                        rows={2}
                        placeholder={f.placeholder ?? ''}
                        onChange={(e) => setField(f.key, e.target.value)}
                        style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.4 }}
                      />
                    ) : (
                      <input
                        value={values[f.key] ?? ''}
                        maxLength={f.maxLen}
                        placeholder={f.placeholder ?? ''}
                        onChange={(e) => setField(f.key, e.target.value)}
                        style={inputStyle}
                      />
                    )}
                  </Field>
                );
              })}

              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <Button
                  onClick={() => void save()}
                  style={{
                    flex: 1,
                    background: C.basil,
                    color: C.basilInk,
                    borderColor: C.basil,
                    opacity: saving || !img ? 0.6 : 1,
                  }}
                >
                  {saving ? 'Saving…' : linkedPost ? 'Save & attach to post' : 'Save to Library'}
                </Button>
              </div>
              {saved &&
                (saved.attached ? (
                  <div style={{ fontSize: 12.5, color: C.good }}>
                    ✓ Attached to your post.{' '}
                    <span
                      onClick={() => navigate('/studio/library')}
                      style={{ color: C.basil, cursor: 'pointer' }}
                    >
                      View post →
                    </span>
                  </div>
                ) : (
                  <div style={{ fontSize: 12.5, color: C.good, lineHeight: 1.6 }}>
                    ✓ Saved to Library.{' '}
                    <span
                      onClick={() => navigate(`/studio/copy?media=${saved.readyId}`)}
                      style={{ color: C.basil, cursor: 'pointer' }}
                    >
                      Write copy for it →
                    </span>
                    <br />
                    <span
                      onClick={() => navigate('/studio/library?view=media&stage=ready')}
                      style={{ color: C.muted, cursor: 'pointer' }}
                    >
                      or view in Library →
                    </span>
                  </div>
                ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  background: C.surface,
  border: `1px solid ${C.line}`,
  borderRadius: 9,
  padding: '9px 12px',
  fontSize: 13,
  color: C.primary,
  fontFamily: 'inherit',
};

const miniBtnStyle: React.CSSProperties = {
  padding: '4px 9px',
  borderRadius: 7,
  border: `1px solid ${C.line}`,
  background: C.surface,
  color: C.secondary,
  fontFamily: 'inherit',
  fontSize: 11,
  cursor: 'pointer',
};

const tileStyle: React.CSSProperties = {
  width: 60,
  height: 60,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  border: `1.5px solid ${C.line}`,
  background: C.surface,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

function Field({
  label,
  on,
  onToggle,
  children,
}: {
  label: string;
  on?: boolean;
  onToggle?: () => void;
  children: React.ReactNode;
}) {
  const off = on === false;
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 7,
        }}
      >
        <div style={{ font: `500 10px 'IBM Plex Mono',monospace`, color: C.faint }}>{label}</div>
        {onToggle && <Switch on={!off} onClick={onToggle} />}
      </div>
      <div style={{ opacity: off ? 0.4 : 1, pointerEvents: off ? 'none' : 'auto' }}>{children}</div>
    </div>
  );
}

/** Small on/off toggle. Green when on. */
function Switch({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onClick}
      title={on ? 'Shown — click to hide' : 'Hidden — click to show'}
      style={{
        width: 30,
        height: 17,
        borderRadius: 9,
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        position: 'relative',
        background: on ? C.basil : C.lineStrong,
        transition: 'background .15s',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: on ? 15 : 2,
          width: 13,
          height: 13,
          borderRadius: '50%',
          background: '#fff',
          transition: 'left .15s',
        }}
      />
    </button>
  );
}
